import _ from 'underscore';
import styled, { useTheme } from 'styled-components';
import { Icon } from '@workramp/ui';
import { theme } from '~/styles';
import StringUtils from '~/common/string-utils';
import { sanitizeUserInput } from '~/utils/sanitize-user-input';
import AssetPickerTypes from '~/components/common/asset-picker/asset-picker-types';
import { getIconPropsForCMSAsset, CMSTopLevelContentTypes } from '~/elc/features/cms';
import LearningObjectIcon from '../../components/common/asset-picker/LearningObjectIcon';

export const GuideSectionSearchResult = ({ guideTitle, name }) => (
  <SearchResultDetails>
    <TruncatedDetail>
      <SearchResultItemDetails type={AssetPickerTypes.GUIDE} name={guideTitle} dim />
    </TruncatedDetail>
    <PrimaryDetail>
      <BreadCrumb src="/images/breadcrumb_chevron.svg" />
      <SearchResultItemDetails type={AssetPickerTypes.GUIDE_SECTION} name={name} />
    </PrimaryDetail>
  </SearchResultDetails>
);

export const GuideTaskSearchResult = ({ content, guideTitle, name, sectionTitle }) => (
  <SearchResultDetails>
    <TruncatedDetail>
      <SearchResultItemDetails type={AssetPickerTypes.GUIDE} name={guideTitle} dim />
    </TruncatedDetail>
    <TruncatedDetail>
      <BreadCrumb src="/images/breadcrumb_chevron.svg" />
      <SearchResultItemDetails type={AssetPickerTypes.GUIDE_SECTION} name={sectionTitle} dim />
    </TruncatedDetail>
    <PrimaryDetail>
      <BreadCrumb src="/images/breadcrumb_chevron.svg" />
      <SearchResultItemDetails content={content} type={AssetPickerTypes.GUIDE_TASK} name={name} />
    </PrimaryDetail>
  </SearchResultDetails>
);

const SearchResultItemDetailsBody = ({
  allowTruncation,
  name,
  parentName,
  parentType,
  content,
  matchedTagContent,
  contentLabelOverrides,
  isExternal,
}) => {
  if (allowTruncation) return <OverflowableText>{name}</OverflowableText>;
  return (
    <div>
      <div
        className="default-search-result--title"
        dangerouslySetInnerHTML={{ __html: sanitizeUserInput(name, { allowSafeTags: true }) }}
      />
      {parentName && (
        <div className="default-search-result--subtitle">
          <span>{parentName} </span>
          <span>
            {StringUtils.getLocalizedTypeFromReadableEnglishType(
              parentType,
              contentLabelOverrides,
              Boolean(isExternal),
            )}
          </span>
        </div>
      )}
      {content && (
        <div
          className="default-search-result--content"
          dangerouslySetInnerHTML={{ __html: sanitizeUserInput(content, { allowSafeTags: true }) }}
        />
      )}
      {!_.isEmpty(matchedTagContent) && <MatchedTags matchedTagContent={matchedTagContent} />}
    </div>
  );
};

export const SearchResultItemDetails = ({
  content,
  dim = false,
  type,
  matchedTagContent,
  name,
  parentName,
  parentType,
  contentLabelOverrides,
  isExternal,
}) => {
  name = name?.trim();
  parentName = parentName?.trim();
  content = content && !Array.isArray(content) ? content.trim() : content;

  let icon = null;
  const themeNew = useTheme();

  if (CMSTopLevelContentTypes.CMS_FILE === type || CMSTopLevelContentTypes.CMS_LINK === type) {
    const assetType =
      CMSTopLevelContentTypes.CMS_FILE === type
        ? APP_CONSTANTS.CMS_ASSET_TYPES.FILE
        : APP_CONSTANTS.CMS_ASSET_TYPES.LINK;

    const iconsProps = getIconPropsForCMSAsset({ assetType }, themeNew.colors);
    icon = (
      <IconWrapper>
        <Icon name={iconsProps.name} color={iconsProps.color} size="md" />
      </IconWrapper>
    );
  } else {
    icon = <LearningObjectIcon type={type} iconClassName="default-search-result--icon" />;
  }

  return (
    <SearchResultDetails dim={dim}>
      {icon}
      <SearchResultDetailLabel className="default-search-result--label">
        <SearchResultItemDetailsBody
          allowTruncation={dim}
          name={name}
          parentName={parentName}
          parentType={parentType}
          content={content}
          matchedTagContent={matchedTagContent}
          contentLabelOverrides={contentLabelOverrides}
          isExternal={isExternal}
        />
      </SearchResultDetailLabel>
    </SearchResultDetails>
  );
};

const MatchedTags = ({ matchedTagContent }) => (
  <MatchedTagWrapper className="default-search-result--content">
    <TagIcon src="/images/tag_solid_gray.svg" />
    <div dangerouslySetInnerHTML={{ __html: sanitizeUserInput(matchedTagContent, { allowSafeTags: true }) }} />
  </MatchedTagWrapper>
);

const SearchResultDetails = styled.div`
  width: 100%;
  overflow: ${(props) => (props.dim ? 'hidden' : 'initial')};
  display: flex;
  align-items: center;
`;

const SearchResultDetailLabel = styled.div`
  padding-right: 0px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BreadCrumb = styled.img`
  height: 7px;
  width: 7px;
  margin: 0 10px;

  @media (max-width: 900px) {
    display: none !important;
  }
`;

const TruncatedDetail = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 1;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  @media (max-width: 900px) {
    display: none !important;
  }
`;

const PrimaryDetail = styled.div`
  flex-shrink: 5;
  flex-grow: 1;
  white-space: nowrap;
  display: flex;
  align-items: center;
`;

const OverflowableText = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-family: ${theme.font.semibold};
  font-size: 0.85rem;
  color: ${(props) => props.theme.colors.on[600]};
  font-weight: 600;
  @media (max-width: 900px) {
    display: none;
  }
`;

const MatchedTagWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TagIcon = styled.img`
  height: 10px !important;
  width: 10px !important;
  margin-right: 3px !important;
`;

const IconWrapper = styled.div`
  min-width: ${(props) => props.theme.spacing(4)};
`;
